import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ChangeEvent, useEffect, useState } from "react";
import { SelectChangeEvent, styled } from "@mui/material";

import {
  CustomButton,
  CustomTable,
  Text,
  CustomSelect,
  CustomRadio,
  CustomCheckBox,
} from "../../../../../shared/uiComponents";
import { SelectDataProperties } from "../../../../../shared/uiComponents/Dropdown";
import { AdminTypes } from "../../../../../components/Menu";
import { TableBody, headers } from "../tableInfo";
import Allocate from "./allocate";

import { DispatchProperties, useSelector } from "../../../../../redux/store";
import {
  getBillingStatus,
  getTherapists,
  updateBilling,
} from "../../../../../redux/State/clientSlice/userClientSlice";
import { getActiveInsuranceAuthorizations } from "../../../../../redux/State/clientSlice/insuranceAuthorizationSlice";
import { ActiveAuthorizationProperties } from "../../../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties";
import DefaultBCBA from "../../../AssignTherapists/defaultBCBA";
import { unwrapResult } from "@reduxjs/toolkit";
import { errorNormalizer } from "../../../../../shared/Helpers/functions";
import { toast } from "react-toastify";

const Wrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",
  width: "300px",
}));

const OPTIONS = [
  { id: "0", title: "For BCBA" },
  { id: "1", title: "For BT" },
];

const AllocateHoursWrapper = () => {
  const role = useSelector((state) => state.account.role);

  if (role?.section.id !== AdminTypes.cs) return <AllocateInsurance />;
  return <AllocateInsuranceClinicalSupervisor />;
};

const AllocateInsuranceClinicalSupervisor = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const { clientId } = useParams();

  const authorizationList = useSelector((state) => state.authorization.authorizationDetails);
  const clinicallySupervisedById = useSelector((state) => state.account.user.id);
  const therapists = useSelector((state) => state.userClient.therapists);
  const [userId, setUserId] = useState<string>("");
  const [authorizations, setAuthorizations] = useState<Array<ActiveAuthorizationProperties>>([]);
  const [billingStatus, setBillingStatus] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!userId || !clientId) return;
    setLoading(true);
    dispatch(getBillingStatus({ clientId, userId }))
      .then(unwrapResult)
      .then((response) => setBillingStatus(response))
      .catch(errorNormalizer)
      .finally(() => setLoading(false));
  }, [userId, clientId, dispatch]);

  useEffect(() => {
    if (!authorizationList) return;
    const authorizations = authorizationList.map((z) => ({
      ...z,
      authorizationDetails: z.authorizationDetails.filter((t) =>
        t.sessionType.name.toLowerCase().includes("assessment")
      ),
    }));
    setAuthorizations(authorizations);
  }, [authorizationList]);

  useEffect(() => {
    if (!clientId || !!authorizationList) return;
    dispatch(getActiveInsuranceAuthorizations({ clientId }));
  }, [clientId, authorizationList, dispatch]);

  useEffect(() => {
    if (!clientId) return;
    dispatch(getTherapists({ clientId, clinicallySupervisedById }));
  }, [clientId, clinicallySupervisedById, dispatch]);

  const onChangeTherapist = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setUserId(value);
  };

  const updateBillingStatus = (billingStopped: boolean) => {
    if (!userId || !clientId) return;
    setBillingStatus(billingStopped);
    dispatch(updateBilling({ clientId, userId, billingStopped }))
      .then(unwrapResult)
      .then(({ isSuccessful }) => {
        toast(isSuccessful ? "Updated" : "Failed");
      })
      .catch(errorNormalizer);
  };

  const therapistList = !!therapists
    ? therapists.map((x) => ({
        id: x.id,
        name: `${x.fullName} (${x.role.name})`,
      }))
    : [];

  return !!authorizationList && !!authorizationList.length ? (
    <>
      <Wrapper className="marginBottom16 marginTop16">
        <CustomSelect
          label="Select Provider"
          data={therapistList}
          value={userId}
          setValue={onChangeTherapist}
          className="marginBottom16 marginTop16"
        />
        {!!userId && (
          <CustomCheckBox
            item={{ id: "0", checked: billingStatus, label: "Lock BCBA Billing", disabled: loading }}
            onChange={(event) => updateBillingStatus(event.target.checked)}
            style={{ marginBottom: "16px" }}
          />
        )}
        <Allocate therapistId={userId} />
      </Wrapper>
      <CustomTable
        TableBody={TableBody}
        headers={headers}
        data={{ query: authorizations }}
        loading={false}
        hasPagination={false}
      />
    </>
  ) : (
    <Wrapper>
      <Text
        title={"Client does not have active insurances"}
        size="smallBold"
        className="marginBottom16"
      />
    </Wrapper>
  );
};

const AllocateInsurance = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [params, setParams] = useSearchParams();

  const therapists = useSelector((state) => state.userClient.therapists);
  const authorizationList = useSelector((state) => state.authorization.authorizationDetails);

  const [userId, setUserId] = useState<string>("");
  const [option, setOption] = useState<string>("1");
  const [therapistsDataBCBA, setTherapistsDataBCBA] = useState<Array<SelectDataProperties>>([]);
  const [therapistsDataBT, setTherapistsDataBT] = useState<Array<SelectDataProperties>>([]);
  const [authorizations, setAuthorizations] = useState<Array<ActiveAuthorizationProperties>>([]);
  const [billingStatus, setBillingStatus] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!userId || !clientId) return;
    setLoading(true);
    dispatch(getBillingStatus({ clientId, userId }))
      .then(unwrapResult)
      .then((response) => setBillingStatus(response))
      .catch(errorNormalizer)
      .finally(() => setLoading(false));
  }, [userId, clientId, dispatch]);

  useEffect(() => {
    if (!authorizationList) return;
    if (option === "1") {
      const btAuthorizations: ActiveAuthorizationProperties[] = [];

      for (const authorization of authorizationList) {
        const filteredDetails = authorization.authorizationDetails.filter(
          (detail) => detail.sessionType.isForRbts
        );

        if (!!filteredDetails.length) {
          btAuthorizations.push({
            ...authorization,
            authorizationDetails: filteredDetails,
          });
        }
      }
      setAuthorizations(btAuthorizations);
      return;
    }
    const bcbaAuthorizations: ActiveAuthorizationProperties[] = [];

    for (const authorization of authorizationList) {
      const filteredDetails = authorization.authorizationDetails.filter(
        (detail) => !detail.sessionType.isForRbts
      );

      if (!!filteredDetails.length) {
        bcbaAuthorizations.push({
          ...authorization,
          authorizationDetails: filteredDetails,
        });
      }
    }
    setAuthorizations(bcbaAuthorizations);
  }, [option, authorizationList]);

  useEffect(() => {
    if (!therapists) return;
    const dataBCBA: Array<SelectDataProperties> = therapists
      .filter((x) => x.role.section.id !== AdminTypes.bt)
      .map((x) => ({
        id: x.id,
        name: `${x.fullName} (${x.role.name})`,
      }));
    const dataBT: Array<SelectDataProperties> = therapists
      .filter((x) => x.role.section.id === AdminTypes.bt)
      .map((x) => ({
        id: x.id,
        name: `${x.fullName}`,
      }));
    setTherapistsDataBCBA(dataBCBA);
    setTherapistsDataBT(dataBT);
  }, [therapists, dispatch]);

  useEffect(() => {
    if (!clientId) return;
    dispatch(getTherapists({ clientId }));
  }, [clientId, dispatch]);

  useEffect(() => {
    if (!clientId || !!authorizationList) return;
    dispatch(getActiveInsuranceAuthorizations({ clientId }));
  }, [clientId, authorizationList, dispatch]);

  const onChangeTherapist = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setUserId(value);
  };

  const navigateAddAuthorizationHandler = () => {
    window.localStorage.setItem("tab", "2");
    navigate(`/clients/client/${clientId}`);
  };

  const therapistRadioHandler = (_: ChangeEvent<HTMLInputElement>, value: string) => {
    setOption(value);
    params.delete("authId");
    setParams(params);
    setUserId("");
  };

  const updateBillingStatus = (billingStopped: boolean) => {
    if (!userId || !clientId) return;
    setBillingStatus(billingStopped);
    dispatch(updateBilling({ clientId, userId, billingStopped }))
      .then(unwrapResult)
      .then(({ isSuccessful }) => {
        toast(isSuccessful ? "Updated" : "Failed");
      })
      .catch(errorNormalizer);
  };

  const showBillingCheckbox =
    therapists?.find((x) => x.id === userId)?.role.section.id === AdminTypes.bcba;

  return !!authorizationList && !!authorizationList.length ? (
    <>
      <Wrapper className="marginBottom16 marginTop16">
        {OPTIONS.map((x) => (
          <CustomRadio id={x.id} label={x.title} value={option} onChange={therapistRadioHandler} />
        ))}
        <CustomSelect
          data={option === "0" ? therapistsDataBCBA : therapistsDataBT}
          value={userId}
          setValue={onChangeTherapist}
          className="marginBottom16 marginTop16"
        />
        {showBillingCheckbox && (
          <CustomCheckBox
            item={{ id: "0", checked: billingStatus, label: "Lock BCBA Billing", disabled: loading }}
            onChange={(event) => updateBillingStatus(event.target.checked)}
            style={{ marginBottom: "16px" }}
          />
        )}
        <Allocate therapistId={userId} />
        <DefaultBCBA />
      </Wrapper>
      <CustomTable
        TableBody={TableBody}
        headers={headers}
        data={{ query: authorizations }}
        loading={false}
        hasPagination={false}
      />
    </>
  ) : (
    <Wrapper>
      <Text
        title={"Client does not have active insurances"}
        size="smallBold"
        className="marginBottom16"
      />
      <CustomButton title="Please, Add authorization" onClick={navigateAddAuthorizationHandler} />
    </Wrapper>
  );
};

export default AllocateHoursWrapper;
